package com.trevorwiebe.timesheet.loads.presentation.admin

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.admin.loadrate.presentation.LoadRateRow
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.mileage.domain.model.Location

@Composable
internal expect fun LoadRateSearchDialog(
    loadRates: List<LoadRate>,
    suggested: List<LoadRate>,
    locations: List<Location>,
    onSelect: (LoadRate?) -> Unit,
    onDismiss: () -> Unit,
)

@Composable
internal fun LoadRateSearchContent(
    loadRates: List<LoadRate>,
    suggested: List<LoadRate>,
    locations: List<Location>,
    onSelect: (LoadRate?) -> Unit,
    onDismiss: () -> Unit,
) {
    var query by remember { mutableStateOf("") }

    val locationsById = locations.associateBy { it.id }
    fun matchesQuery(rate: LoadRate): Boolean {
        val q = query.trim()
        if (q.isEmpty()) return true
        return listOfNotNull(
            locationsById[rate.pickupLocationId],
            locationsById[rate.dropLocationId],
        ).any { it.name.contains(q, ignoreCase = true) || it.city.contains(q, ignoreCase = true) }
    }

    val visibleSuggested = suggested.filter(::matchesQuery)
    val visibleOthers = (loadRates - suggested.toSet()).filter(::matchesQuery)

    Surface(
        modifier = Modifier.fillMaxSize(),
        color = MaterialTheme.colorScheme.background
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
                .navigationBarsPadding()
                .imePadding()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp, bottom = 8.dp, end = 16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Close")
                }
                Text(text = "Select Rate", style = MaterialTheme.typography.titleLarge)
            }
            Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                TimesheetTextField(
                    text = query,
                    placeholder = "Search by pickup or drop location",
                    onTextChange = { query = it }
                )
            }
            Spacer(Modifier.height(8.dp))
            LazyColumn(modifier = Modifier.fillMaxWidth().weight(1f)) {
                item {
                    Text(
                        text = "None",
                        style = MaterialTheme.typography.bodyLarge,
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { onSelect(null) }
                            .padding(16.dp)
                    )
                    HorizontalDivider()
                }
                if (visibleSuggested.isNotEmpty()) {
                    item { RateSectionHeader("Matches pickup & drop") }
                    items(visibleSuggested) { loadRate ->
                        LoadRateRow(
                            loadRate = loadRate,
                            locations = locations,
                            onClick = { onSelect(loadRate) }
                        )
                    }
                }
                if (visibleOthers.isNotEmpty()) {
                    if (visibleSuggested.isNotEmpty()) {
                        item { RateSectionHeader("Other rates") }
                    }
                    items(visibleOthers) { loadRate ->
                        LoadRateRow(
                            loadRate = loadRate,
                            locations = locations,
                            onClick = { onSelect(loadRate) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun RateSectionHeader(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = Modifier.padding(start = 16.dp, top = 12.dp, bottom = 4.dp)
    )
}
